import { Box, Button, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getServerErrorMessage } from 'utils/utils';

type DataTableErrorStateProps = {
    error?: unknown;
    onRefetch?: () => void;
};

const SUPPORT_MESSAGE =
    'مشکلی در دریافت اطلاعات پیش آمده است. لطفاً با پشتیبانی ارتباط بگیرید.';

const looksLikeTechnicalError = (message: string): boolean =>
    /(attempt to|undefined|sqlstate|exception|stack trace|syntax error|property .* on null|call to .* on null)/i.test(
        message,
    );

const normalizeErrorMessage = (error?: unknown): string => {
    const queryError = error as
        | { status?: string | number; error?: string }
        | undefined;

    if (queryError?.status === 'FETCH_ERROR') {
        return 'ارتباط با سرور برقرار نشد. اتصال شبکه را بررسی و دوباره تلاش کنید.';
    }

    if (queryError?.status === 'PARSING_ERROR') {
        return SUPPORT_MESSAGE;
    }

    if (queryError?.status === 'TIMEOUT_ERROR') {
        return 'پاسخ سرور بیش از حد طول کشید. چند لحظه دیگر دوباره تلاش کنید.';
    }

    const message = getServerErrorMessage(error, '');
    const numericStatus =
        typeof queryError?.status === 'number' ? queryError.status : undefined;

    if (numericStatus !== undefined && numericStatus >= 500) {
        return SUPPORT_MESSAGE;
    }

    if (message && !looksLikeTechnicalError(message)) {
        return message === 'Internal server error.' ? SUPPORT_MESSAGE : message;
    }

    return SUPPORT_MESSAGE;
};

const DataTableErrorState = ({ error, onRefetch }: DataTableErrorStateProps) => (
    <Box
        role="alert"
        sx={(theme) => ({
            mx: { xs: 2, md: 3 },
            my: 2.5,
            p: { xs: 2, sm: 2.25 },
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            border: '1px solid',
            borderColor:
                theme.palette.mode === 'dark'
                    ? alpha(theme.palette.error.light, 0.25)
                    : alpha(theme.palette.error.main, 0.2),
            borderRadius: '12px',
            bgcolor:
                theme.palette.mode === 'dark'
                    ? alpha(theme.palette.error.main, 0.055)
                    : alpha(theme.palette.error.main, 0.025),
        })}
    >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.4, minWidth: 0 }}>
            <Box
                sx={(theme) => ({
                    width: 38,
                    height: 38,
                    flex: '0 0 auto',
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: '10px',
                    color:
                        theme.palette.mode === 'dark'
                            ? theme.palette.error.light
                            : theme.palette.error.main,
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                })}
            >
                <i className="fa-light fa-triangle-exclamation" />
            </Box>

            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    دریافت اطلاعات انجام نشد
                </Typography>
                <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 0.45, color: 'text.secondary', lineHeight: 1.9 }}
                >
                    {normalizeErrorMessage(error)}
                </Typography>
            </Box>
        </Box>

        {onRefetch && (
            <Button
                variant="contained"
                size="small"
                disableElevation
                onClick={onRefetch}
                startIcon={<i className="fa-solid fa-rotate" />}
                sx={(theme) => ({
                    flexShrink: 0,
                    minWidth: 110,
                    fontWeight: 700,
                    ...(theme.palette.mode === 'dark'
                        ? {
                              bgcolor: theme.palette.primary.light,
                              color: theme.palette.common.white,
                              border: '1px solid',
                              borderColor: alpha(
                                  theme.palette.primary.light,
                                  0.7,
                              ),
                              boxShadow: `0 0 0 1px ${alpha(
                                  theme.palette.primary.light,
                                  0.14,
                              )}`,
                              '&:hover': {
                                  bgcolor: theme.palette.primary.main,
                              },
                          }
                        : {}),
                })}
            >
                تلاش مجدد
            </Button>
        )}
    </Box>
);

export default DataTableErrorState;
